/**
 * PostHog product analytics — the analytics twin of src/instrument.js.
 *
 * Imported from main.jsx before App renders, so the library is ready before
 * the first page view fires.
 *
 * Replaced Google Analytics (August 2026). GA had been in the app since 22 May
 * 2026 and never once reported: helmet's default Content-Security-Policy on the
 * backend blocks any script that isn't our own, so the googletagmanager.com tag
 * was refused by the browser on every single production page load. If PostHog
 * ever goes quiet in production, check that CSP first — see the `posthog`
 * entries in the contentSecurityPolicy block of backend/src/index.js. It is the
 * one piece of this setup that lives outside this file.
 *
 * Four things this does that a copy-pasted snippet would not:
 *
 *   1. Never lets a live token reach PostHog. Password-reset and email-verify
 *      links carry one in ?token=..., and those pages are opened straight from
 *      an email — so they are the FIRST page load of the session, which is
 *      exactly when PostHog records its permanent "initial" properties. See
 *      the long note on startWhenUrlIsClean() below; this took three attempts
 *      to get right and the two obvious fixes both silently don't work.
 *   2. Stays off outside production, so local dev traffic doesn't pollute the
 *      same project as real users.
 *   3. Sends page views manually, because React Router changes the URL without
 *      reloading the page.
 *   4. Identifies people by database id only — never by email. Consistent with
 *      `sendDefaultPii: false` in instrument.js: an analytics store shouldn't
 *      quietly become a second copy of the user table.
 *
 * Worth knowing: VITE_ variables are baked into the bundle at BUILD time, not
 * read at runtime — same gotcha as VITE_SENTRY_DSN. Changing the key on Render
 * does nothing until the next deploy.
 */

import posthog from 'posthog-js'

// No fallback key on purpose. An unset key means "don't report", which is what
// a laptop and a preview build should both do. The old GA module defaulted to
// a hardcoded live id, which is how a dev build could have reported into the
// production property.
const apiKey = import.meta.env.VITE_POSTHOG_KEY

// PostHog's US cloud. Set VITE_POSTHOG_HOST to https://eu.i.posthog.com if the
// project is ever moved to the EU region — the CSP in backend/src/index.js
// already allows both, so that's the only change needed.
const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

// Only report from real deploys. `PROD` is true for `vite build` output and
// false for `vite dev`, so localhost is silent by default.
// VITE_POSTHOG_DEBUG=true forces it on locally when you need to confirm events
// actually arrive — note that they land in whatever project the key points at.
const enabled = Boolean(apiKey) &&
  (import.meta.env.PROD || import.meta.env.VITE_POSTHOG_DEBUG === 'true')

// Deliberately the same pattern as scrubUrl() in src/instrument.js — if you
// add a sensitive parameter name there, add it here too.
function scrubUrl(url) {
  if (typeof url !== 'string') return url
  return url.replace(/([?&][^=&]*(?:token|key|secret|jwt|code)[^=&]*=)[^&]*/gi, '$1[Redacted]')
}

/** True when a URL carries something that must never reach PostHog. */
function hasSecret(url) {
  return scrubUrl(url) !== url
}

// Properties PostHog fills in by itself that can contain a full URL, query
// string and all. Autocapture attaches these to every click, so scrubbing only
// the page-view event would still leak via the first thing the user clicked.
//
// This list is not from the docs — it was built by pointing the app at a local
// stand-in for PostHog's ingest endpoint, loading /reset-password?token=..., and
// reading every property that arrived still carrying the token. $session_entry_*
// were the ones that would otherwise have been missed.
const URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$pathname',
  '$initial_current_url',
  '$initial_referrer',
  '$session_entry_url',
  '$session_entry_referrer',
]

// PostHog hands every outbound event through here on its way out — autocaptured
// clicks included. Returning null would drop the event entirely; we only ever
// rewrite the URL properties and pass it on.
//
// This is `before_send`, not the older `sanitize_properties`: that one is
// deprecated in posthog-js 1.417 and warns on every init.
function beforeSend(captureResult) {
  if (!captureResult?.properties) return captureResult
  for (const key of URL_PROPERTIES) {
    if (typeof captureResult.properties[key] === 'string') {
      captureResult.properties[key] = scrubUrl(captureResult.properties[key])
    }
  }
  return captureResult
}

// Cookie consent (see components/CookieConsent.jsx). PostHog sets cookies —
// autocapture, session replay, the distinct_id — so it must not start until
// the visitor has actually said yes, not merely because a key is configured.
const CONSENT_KEY = 'jai_cookie_consent'

function hasStoredConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'granted'
  } catch {
    return false
  }
}

/** null = no decision yet, 'granted' | 'denied' otherwise. */
export function getConsentDecision() {
  try {
    return localStorage.getItem(CONSENT_KEY)
  } catch {
    return null
  }
}

let started = false

// Set when identifyUser() is called before PostHog has started — which happens
// on any visit that begins on a tokenised URL, because AuthContext restores a
// saved session while startWhenUrlIsClean() is still holding off. Without this
// the whole visit would be recorded as anonymous.
let pendingUser = null

function startPostHog() {
  if (started) return
  started = true

  posthog.init(apiKey, {
    api_host: apiHost,

    // We send page views ourselves in trackPageView(), because React Router
    // swaps screens without a page load.
    capture_pageview: false,

    // Cheap and genuinely useful: tells you how long someone stayed on a
    // screen, which is how you spot the step people give up on.
    capture_pageleave: true,

    // Records clicks, form submits and input focus automatically, so you can
    // answer "did anyone click that button" later without having had the
    // foresight to instrument it. Set to false for manual events only.
    autocapture: true,

    // The cost control that matters. Without this, every anonymous visitor
    // creates a stored person profile and burns quota; with it, profiles are
    // only created once someone signs in and we call identifyUser().
    person_profiles: 'identified_only',

    // Session replay. Delete this block (or set disable_session_recording to
    // true) to turn recording off entirely — nothing else needs to change,
    // though the privacy policy mentions replay, so update section 9 too.
    disable_session_recording: false,
    session_recording: {
      // Every <input>, <textarea> and <select> value is replaced with asterisks
      // before it leaves the browser. This is what keeps passwords, emails and
      // anything typed into a note out of the recording.
      maskAllInputs: true,

      // Opt-in masking for rendered text: put data-private on any element and
      // its contents are blanked out in the replay too.
      maskTextSelector: '[data-private]',

      // Replay records the network calls made during a session, which means the
      // API URLs.
      //
      // The URL arrives as `name`, NOT `url`, and the returned object is read
      // back off `name` too. Verified against posthog-js 1.417.1 rather than
      // remembered: the older `maskNetworkRequestFn` hook takes `{ url }`
      // instead, so mixing the two up gives you a masking function that runs
      // happily on `undefined` and scrubs nothing at all. Don't define both —
      // PostHog warns and ignores the legacy one.
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: scrubUrl(request.name),
      }),
    },

    // The catch-all: every event, autocaptured or manual, goes through here on
    // its way out.
    before_send: beforeSend,

    // PostHog ignores Do Not Track unless told otherwise. This must stay true:
    // the privacy policy (section 9) tells users that turning on Do Not Track
    // stops us recording them, and a policy that promises something the code
    // doesn't do is worse than not offering it at all.
    respect_dnt: true,

    // PostHog's own console noise, only when we've explicitly asked for it.
    loaded: (ph) => {
      if (import.meta.env.VITE_POSTHOG_DEBUG === 'true') ph.debug()
    },
  })
}

/**
 * Start PostHog, but never while a secret is sitting in the address bar.
 *
 * THE PROBLEM. On top of each event's own properties, PostHog sends a separate
 * top-level `person_properties` block holding $initial_current_url and friends.
 * They are captured once, from window.location, and then stored against the
 * person permanently. Password-reset and verify links are opened directly from
 * an email, so the tokenised URL isn't some rare edge case — it is the first
 * page load of the session, which is precisely the load those properties are
 * taken from.
 *
 * WHAT DOESN'T WORK, both confirmed against a local capture of the real
 * outbound payloads rather than assumed:
 *
 *   - `before_send` never sees person_properties. It cleans the events and
 *     leaves the leak completely untouched.
 *   - `property_denylist: ['$initial_current_url']` does not filter that block
 *     either. The token came through with it set.
 *   - Swapping the address bar to a scrubbed URL for the duration of init()
 *     also fails: the value is written to localStorage after init's synchronous
 *     phase, by which point the real URL is back.
 *   - `advanced_disable_flags: true` does stop it, because the /flags/ request
 *     is what carries the block — but it also switches off remote config, and
 *     session replay never starts without that. Verified: with flags disabled,
 *     $session_recording_enabled_server_side is never set.
 *
 * WHAT WORKS. Simply don't start PostHog while the URL holds a secret. On a
 * tokenised page nothing initialises, so there is nothing to leak; the moment
 * the user moves to any ordinary screen, trackPageView() starts it with a clean
 * URL and everything from that point on is recorded normally.
 *
 * The cost is analytics on the reset and verify screens themselves, which is a
 * rounding error of traffic and not something anyone would analyse. In exchange
 * this needs no PostHog internals, so a library update can't quietly break it.
 */
function startWhenUrlIsClean() {
  if (started || hasSecret(window.location.href)) return
  startPostHog()
  // Apply an identify that arrived while we were waiting.
  if (pendingUser) {
    const user = pendingUser
    pendingUser = null
    identifyUser(user)
  }
}

// Auto-starts only on a return visit that already granted consent. A first
// visit waits for CookieConsent.jsx to call grantConsent().
if (enabled && hasStoredConsent()) startWhenUrlIsClean()

/** Called by the cookie banner when the visitor accepts. */
export function grantConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, 'granted')
  } catch {
    // Storage blocked (private mode, disabled cookies) — nothing to persist,
    // but still start this session since the visitor did say yes.
  }
  if (enabled) startWhenUrlIsClean()
}

/** Called by the cookie banner when the visitor declines. */
export function denyConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, 'denied')
  } catch {
    // Nothing to persist — PostHog simply never starts this session.
  }
}

/**
 * Record a page view. Called on every route change, with the token stripped
 * out of the URL first.
 */
export function trackPageView(path) {
  if (!enabled) return
  // Also the retry hook for a visit that began on a tokenised URL — this is the
  // first thing called after React Router moves to a new screen.
  startWhenUrlIsClean()
  if (!started) return

  const cleanPath = scrubUrl(path)
  posthog.capture('$pageview', {
    $current_url: scrubUrl(window.location.origin + path),
    $pathname: cleanPath,
  })
}

/**
 * Record something a user did — a signup, a league connect, a checkout start.
 *
 * Keep `params` to non-identifying values: counts, plan names, platform names.
 * Never pass an email, a league name, a note body or anything else that could
 * identify a person or reveal their data — an analytics project is not a place
 * we want personal information accumulating, because everything that lands
 * there is something we then have to disclose, protect and delete on request.
 */
export function trackEvent(name, params = {}) {
  if (!enabled || !started) return
  posthog.capture(name, params)
}

/**
 * Tie the events from this browser to a known account.
 *
 * Called on login, on signup, and on restoring a saved session. Deliberately
 * sends the database id and nothing that names the person: it's enough to
 * follow one account through the funnel without copying the user table into a
 * third party.
 *
 * `plan` is a person property rather than an event property so PostHog can
 * answer "how do Pro users behave differently" — it's attached to the person
 * and updates whenever they upgrade.
 */
export function identifyUser(user) {
  if (!enabled || !user?.id) return
  // Still waiting on a clean URL — hold it and apply it once PostHog starts.
  if (!started) {
    pendingUser = user
    return
  }
  posthog.identify(String(user.id), {
    plan: user.plan || 'starter',
    is_guest: Boolean(user.isGuest),
  })
}

/**
 * Forget the current account on logout.
 *
 * Without this, PostHog keeps the same distinct_id after sign-out, so if a
 * second person signs in on a shared laptop their activity is recorded against
 * the first person's profile.
 */
export function resetUser() {
  if (!enabled) return
  // Drop a held identify too, or someone who signed out before PostHog started
  // would be identified anyway a moment later.
  pendingUser = null
  if (!started) return
  posthog.reset()
}
